"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useUser } from "@/features/auth/hooks/useUser";
import type { WatchlistEnrichedItem } from "../types";
import { capture } from "@/lib/analytics/posthog";

/** Minimal item used by add/toggle (game pages only know these fields). */
export type WatchlistAddPayload = {
  appid: number;
  name: string;
  capsule_image: string | null;
};

type WatchlistContextValue = {
  items: WatchlistEnrichedItem[];
  loading: boolean;
  has: (appid: number) => boolean;
  add: (item: WatchlistAddPayload) => Promise<void>;
  remove: (appid: number) => Promise<void>;
  toggle: (item: WatchlistAddPayload) => Promise<void>;
};

const WatchlistContext = createContext<WatchlistContextValue>({
  items: [],
  loading: false,
  has: () => false,
  add: async () => {},
  remove: async () => {},
  toggle: async () => {},
});

function makeOptimisticItem(item: WatchlistAddPayload): WatchlistEnrichedItem {
  return {
    appid: item.appid,
    name: item.name,
    capsule_image: item.capsule_image,
    added_at: new Date().toISOString(),
    type: null,
    is_free: null,
    release_date: null,
    price_final: null,
    price_initial: null,
    price_discount: null,
    ccu_online: null,
    ccu_all_time_peak: null,
    topsellers_rank: null,
    topsellers_best_all_time_rank: null,
    review_count: null,
    percent_positive: null,
    review_score: null,
    review_score_label: null,
    first_week_reviews: null,
    first_week_positive: null,
  };
}

export function WatchlistProvider({ children }: { children: ReactNode }) {
  const { user, plan } = useUser();
  const isAuth = !!user || plan !== "anonymous";

  const [items, setItems] = useState<WatchlistEnrichedItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch watchlist when user signs in
  useEffect(() => {
    if (!isAuth) {
      setItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch("/api/watchlist")
      .then((r) => (r.ok ? r.json() : []))
      .then((data: WatchlistEnrichedItem[]) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuth]);

  const has = useCallback(
    (appid: number) => items.some((i) => i.appid === appid),
    [items],
  );

  const add = useCallback(
    async (item: WatchlistAddPayload) => {
      // Optimistic update
      const optimistic = makeOptimisticItem(item);
      setItems((prev) => [optimistic, ...prev.filter((i) => i.appid !== item.appid)]);

      try {
        const res = await fetch("/api/watchlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(item),
        });
        if (!res.ok) throw new Error("Failed");
        // Reported after the server confirms, not on the optimistic write —
        // a rolled-back add must not show up as an add.
        capture("watchlist toggled", { appid: item.appid, added: true });
      } catch {
        // Rollback
        setItems((prev) => prev.filter((i) => i.appid !== item.appid));
      }
    },
    [],
  );

  const remove = useCallback(async (appid: number) => {
    const prev = items.find((i) => i.appid === appid);
    // Optimistic update
    setItems((cur) => cur.filter((i) => i.appid !== appid));

    try {
      const res = await fetch(`/api/watchlist/${appid}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed");
      capture("watchlist toggled", { appid, added: false });
    } catch {
      // Rollback
      if (prev) setItems((cur) => [prev, ...cur]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const toggle = useCallback(
    async (item: WatchlistAddPayload) => {
      if (has(item.appid)) {
        await remove(item.appid);
      } else {
        await add(item);
      }
    },
    [has, add, remove],
  );

  const value = useMemo(
    () => ({ items, loading, has, add, remove, toggle }),
    [items, loading, has, add, remove, toggle],
  );

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist(): WatchlistContextValue {
  return useContext(WatchlistContext);
}
